import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
  useCallback,
} from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "./supabase";

/**
 * G2 terminal-auth session validation state.
 * - "pending": a local session exists but the backend has not confirmed it yet.
 * - "ok": the backend accepted the session (or the failure was transient).
 * - "terminated": the backend actively rejected the session (revoked session,
 *   blocked account, or G2 `access_locked`) and the local session was cleared.
 */
export type SessionValidationState = "pending" | "ok" | "terminated";

export interface SessionValidationOutcome {
  ok: boolean;
  status?: number;
  errorCode?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  sessionValidation: SessionValidationState;
  reportSessionValidation: (outcome: SessionValidationOutcome) => void;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<{ error: any }>;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionValidation, setSessionValidation] =
    useState<SessionValidationState>("pending");

  // G2 terminal-auth handling. The admin app's bootstrap fetch is the
  // UserProfileContext `GET /users/me` call; it reports its outcome here so
  // this provider can destroy a backend-rejected local session.
  //
  // Contract (structured-code-first):
  // - 403 `trial_expired`   → untouched; the trialExpiredHandler axios
  //   interceptor in restApiService redirects to /trial-expired and the
  //   session stays in place.
  // - 403 `access_locked`   → clear the local session; the route guard sends
  //   the user to the app-web login surface.
  // - other 401/generic 403 → same as access_locked.
  // - network errors / 5xx  → transient; NEVER clear the session for these.
  const reportSessionValidation = useCallback(
    ({ ok, status, errorCode }: SessionValidationOutcome) => {
      if (ok) {
        setSessionValidation("ok");
        return;
      }
      if (status === 401 || status === 403) {
        if (errorCode === "trial_expired") {
          // Existing contract, unchanged: the axios interceptor has already
          // redirected to /trial-expired. Keep the session.
          setSessionValidation("ok");
          return;
        }
        // Revoked session, blocked account, or G2 access_locked — destroy
        // the local session so no authenticated shell can render again.
        void supabase.auth.signOut({ scope: "local" }).catch(() => undefined);
        setUser(null);
        setSessionValidation("terminated");
        return;
      }
      // Network / 5xx — transient. Never clear credentials on these.
      setSessionValidation("ok");
    },
    []
  );

  // A freshly signed-in user gets a fresh validation cycle.
  useEffect(() => {
    if (user) {
      setSessionValidation((current) =>
        current === "terminated" ? "pending" : current
      );
    }
  }, [user]);

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      setLoading(false);
    };

    getInitialSession();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    // Record revocation on backend before clearing local session
    const apiBaseUrl = process.env.REACT_APP_BACKEND_BASE_URL;
    if (apiBaseUrl) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          await fetch(`${apiBaseUrl}/auth/signout`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
          });
        }
      } catch {
        // Best effort — proceed with local sign-out regardless
      }
    }

    await supabase.auth.signOut({ scope: 'global' });
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    return { error };
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        sessionValidation,
        reportSessionValidation,
        signOut,
        signInWithGoogle,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
